import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, withLatestFrom, map, catchError } from 'rxjs/operators';
import { ConnectExcelService } from 'app/connect/core/connect-excel.service';
import {
  EExcelConnectCurrentActions,
  GetDetailFromVersion,
  GetDetailFromVersionSuccess,
  UploadExcelFile,
  UploadExcelFileSuccess,
  DownloadExcelFile,
  DownloadExcelFileSuccess,
  UploadExcelFileError
} from '../actions/excel-connector-current.actions';
import { getCurrentExcelConnectorDetailVersion } from '../selectors/excel-connector-current.selector';
import { select, Store } from '@ngrx/store';
import { UtilsService } from 'app/environment/services/utils.service';
import { BzgToastService, BzgToastType } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { saveFile } from 'app/app.module';
import { IAppState } from 'app/IAppState';
import { I18nService } from 'i18n/i18n.service';
import { ShowToastService } from 'app/shared/services/showToast.service';

@Injectable()
export class ExcelConnectorCurrentEffects {

  getExcelConnectorDetailVersion$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetDetailFromVersion>(EExcelConnectCurrentActions.GetDetailFromVersion),
      switchMap((param: any) => this._connectExcelService.getExcelConnectorDetailVersion(param.payload)),
      switchMap((response: IExcelVersionConnector) => of(new GetDetailFromVersionSuccess(response)))
    ));
  uploadExcelVersionConnector$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UploadExcelFile>(EExcelConnectCurrentActions.UploadExcelFile),
      withLatestFrom(this._store.pipe(select(getCurrentExcelConnectorDetailVersion))),
      map(([payload, excelVersion]) => ({ ...payload, excelVersion })),
      switchMap((param: any) => {
        return this._connectExcelService.uploadExcelFile(param.excelVersion.excelFile.id, param.payload, param.excelVersion.id);
      }),
      switchMap((response) => {
        if (!response.isValid) {
          this.buildMessages(response);
          this._store.dispatch(new UploadExcelFileError());
        } else {
          this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
          return of(new UploadExcelFileSuccess(response.name));
        }

      }),
      catchError((err, caught) => {
        this._store.dispatch(new UploadExcelFileError());
        return caught;
      })
    ));
  downloadExcelFileConnector$ = createEffect(() =>
    this._actions$.pipe(
      ofType<DownloadExcelFile>(EExcelConnectCurrentActions.DownloadExcelFile),
      switchMap((param: any) => {
        return this._connectExcelService.downloadExcelFile(param.payload);
      }),
      switchMap((response: any) => {
        this.getFileFromResponse(response);
        return of(new DownloadExcelFileSuccess(response))
      })
    ));


  constructor(
    private _connectExcelService: ConnectExcelService,
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _toastService: BzgToastService,
    private _translateService: TranslateService,
    private _utilsService: UtilsService,
    private _i18nService: I18nService,
    private _showToastService: ShowToastService
  ) {
  }

  getFileFromResponse(result) {
    let fileName: string = this._utilsService.getFileNameFromHeaders(result.headers, '');
    fileName = _.replace(fileName, new RegExp('"', 'g'), '');
    if (navigator.appVersion.toString().indexOf('.NET') > 0) {
      saveFile(new Blob([result.body], { type: 'application/octet-stream' }), fileName);
    }
    else {
      const pdfUrl = (window.URL || window['webkitURL']).createObjectURL(new Blob([result.body], { type: 'application/octet-stream' }));
      const anchor = document.createElement('a');
      anchor.download = fileName;
      anchor.href = pdfUrl;
      anchor.click();
    }
  }

  private buildMessages(response) {
    const self = this;
    if (response.cellNames.length > 0) {
      let errorMessage = '';
      response.cellNames.forEach(function (element) {
        self._translateService.get('EXCEL_CONNECTOR.CELL_NAME_VALIDATION',
          { cellname: element.CellRangeName, worksheetname: element.WorksheetName }).subscribe((message: string) => {
            errorMessage += message + '\n';
          });
      });
      this._showToastService.showToast(errorMessage, 'error');
    }
    if (response.rangeNames.length > 0) {
      let errorMessage = '';
      response.rangeNames.forEach(function (element) {
        self._translateService.get('EXCEL_CONNECTOR.RANGE_NAME_VALIDATION',
          { rangename: element.CellRangeName, worksheetname: element.WorksheetName }).subscribe((message: string) => {
            errorMessage += message + '\n';
          });
      });
      this._showToastService.showToast(errorMessage, 'error');
    }
    if (response.sheetNames.length > 0) {
      let errorMessage = '';
      response.sheetNames.forEach(function (element) {
        self._translateService.get('EXCEL_CONNECTOR.SHEET_NAME_VALIDATION',
          { sheetname: element }).subscribe((message: string) => {
            errorMessage += message + '\n';
          });
      });
      this._showToastService.showToast(errorMessage, 'error');
    }

    this._showToastService.showToast(this._i18nService.instant('EXCEL_CONNECTOR.FILE_NOT_UPDATED'), 'warning');
  }
}
